import type { MouseEvent } from "react";
import { Link } from "react-router";
import { styled } from "styled-components";

import { dimensionsApiSlice } from "api/dimensionApi.slice";
import { SectionTitleUnderlined, StyledGrid } from "common/components";
import { useSelector } from "common/utilities/react-redux";
import { useGo } from "common/utilities/useGo";
import { useTrack } from "common/utilities/useTrack";
import { useTranslate } from "common/utilities/useTranslate";
import type { Dimension } from "domains/dimension/dimension.types";
import { KeyIssueTile } from "domains/topic/components/KeyIssueTile";
import type { RootState } from "types/redux";

const StyledContainer = styled.div`
  margin-bottom: 2rem;
`;

const StyledItem = styled(Link)`
  cursor: pointer;
  text-decoration: none;
`;

export const KeyIssueResults = () => {
  const go = useGo();
  const track = useTrack();
  const translate = useTranslate();

  const currentSearchQuery = useSelector((state: RootState) => state.search.currentSearchQuery);

  const { data: paginatedDimensionSearchResults, isFetching } =
    dimensionsApiSlice.endpoints.searchDimensions.useInfiniteQuery(
      {
        search: currentSearchQuery,
      },
      { skip: currentSearchQuery.length === 0 },
    );

  const dimensionsSearchResults = paginatedDimensionSearchResults?.pages.flat() ?? [];

  const onKeyIssueClickHandler = (issue: Dimension) => (event: MouseEvent) => {
    event.preventDefault();

    track({
      entityId: issue.parent_topic_id,
      entityType: "Topic",
      name: "Browse page",
      page: "Search",
      secondaryEntityId: issue.id,
      secondaryEntityName: issue.name,
      secondaryEntityType: "Key Issue",
      subpage: "Topic detail",
    });

    go(`/topics/${issue.parent_topic_id}/key-issues/${issue.id}`);
  };

  if (currentSearchQuery.length === 0 || dimensionsSearchResults.length === 0) return null;

  return (
    <StyledContainer>
      <SectionTitleUnderlined isLoading={isFetching} id="keyIssues">
        {translate("app.topic.key_issues")}
      </SectionTitleUnderlined>
      <StyledGrid numberOfItems={[1, 2, 3, 3, 4]}>
        {dimensionsSearchResults.map((issue) => (
          <StyledItem key={issue.id} onClick={onKeyIssueClickHandler(issue)} to={`/topics/${issue.parent_topic_id}`}>
            <KeyIssueTile
              name={issue.name}
              image_url={issue.image_url ?? undefined}
              parent_topic_id={issue.parent_topic_id}
              description={issue.description}
              highlightedTerm={currentSearchQuery}
              shouldHideExpandChevron
            />
          </StyledItem>
        ))}
      </StyledGrid>
    </StyledContainer>
  );
};
